import { forwardRef, useRef, useImperativeHandle, useEffect, useState, useCallback, type HTMLAttributes } from 'react'
import { scrollableListVariants } from './ScrollableList.variants'

export interface ScrollableListProps extends HTMLAttributes<HTMLDivElement> {
  snap?: 'none' | 'mandatory' | 'proximity'
  hideScrollbar?: boolean
  enableDragScroll?: boolean
  enableWheelScroll?: boolean
}

export const ScrollableList = forwardRef<HTMLDivElement, ScrollableListProps>(
  ({ 
    children, 
    snap = 'mandatory', 
    hideScrollbar = true,
    enableDragScroll = true,
    enableWheelScroll = true,
    className, 
    ...props 
  }, ref) => {
    const internalRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)
    
    useImperativeHandle(ref, () => internalRef.current as HTMLDivElement)
    
    // Handle horizontal scroll with mouse wheel
    useEffect(() => {
      const element = internalRef.current
      if (!element || !enableWheelScroll) return
      
      const handleWheel = (e: WheelEvent) => {
        // Only prevent default if we're actually scrolling horizontally
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault()
          element.scrollLeft += e.deltaY
        }
      }
      
      element.addEventListener('wheel', handleWheel, { passive: false })
      return () => { element.removeEventListener('wheel', handleWheel) }
    }, [enableWheelScroll])
    
    // Handle drag to scroll
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      if (!enableDragScroll || !internalRef.current) return
      
      setIsDragging(true)
      setStartX(e.pageX - internalRef.current.offsetLeft)
      setScrollLeft(internalRef.current.scrollLeft)
      
      // Change cursor
      internalRef.current.style.cursor = 'grabbing'
      internalRef.current.style.userSelect = 'none'
    }, [enableDragScroll])
    
    const handleMouseLeave = useCallback(() => {
      if (!isDragging || !internalRef.current) return
      
      setIsDragging(false)
      internalRef.current.style.cursor = 'grab'
      internalRef.current.style.userSelect = ''
    }, [isDragging])
    
    const handleMouseUp = useCallback(() => {
      if (!isDragging || !internalRef.current) return
      
      setIsDragging(false)
      internalRef.current.style.cursor = 'grab'
      internalRef.current.style.userSelect = ''
    }, [isDragging])
    
    const handleMouseMove = useCallback((e: React.MouseEvent) => {
      if (!isDragging || !internalRef.current) return
      
      e.preventDefault()
      const x = e.pageX - internalRef.current.offsetLeft
      const walk = (x - startX) * 1.5 // Scroll speed multiplier
      internalRef.current.scrollLeft = scrollLeft - walk
    }, [isDragging, startX, scrollLeft])
    
    // Set initial cursor style
    useEffect(() => {
      if (!internalRef.current || !enableDragScroll) return
      
      internalRef.current.style.cursor = 'grab'
    }, [enableDragScroll])
    
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- This div needs mouse event handlers for custom drag-to-scroll functionality
      <div
        className={scrollableListVariants({ snap, className, hideScrollbar })}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        ref={internalRef}
        {...props}
      >
        {children}
      </div>
    )
  }
)

ScrollableList.displayName = 'ScrollableList'
